/**
 * Markdown, rendered as you type.
 *
 * A rich-text editor would have meant either a dependency the size of the
 * editor itself or a contenteditable of our own with its own paste bugs. You
 * write markdown and see it set: the stored text stays plain, greppable and
 * exactly what you typed, which is the point of `body_md` being markdown in
 * the first place.
 *
 * Everything is escaped before a single tag is emitted, so no input can put
 * markup into the page — the only tags in the output are the ones this file
 * writes.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Inline run: code first, so nothing inside backticks is read as markup. */
function inline(text: string): string {
  const code: string[] = [];
  // A private-use character as the placeholder: it cannot appear in escaped
  // HTML, and unlike NUL it is not a control character the linter rejects.
  const MARK = '\uE000';
  let out = escapeHtml(text).replace(/`([^`]+)`/g, (_, body: string) => {
    code.push(body);
    return `${MARK}${code.length - 1}${MARK}`;
  });

  out = out
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<s>$1</s>')
    // Only http(s) links become anchors: a bare [x](javascript:…) stays text.
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

  return out.replace(/\uE000(\d+)\uE000/g, (_, i: string) => `<code>${code[Number(i)] ?? ''}</code>`);
}

export function renderMarkdown(source: string): string {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let fence: string[] | null = null;

  const closeList = () => {
    if (list) html.push(`</${list}>`);
    list = null;
  };
  const openList = (kind: 'ul' | 'ol') => {
    if (list === kind) return;
    closeList();
    html.push(`<${kind}>`);
    list = kind;
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');

    if (line.trim().startsWith('```')) {
      if (fence) {
        html.push(`<pre><code>${escapeHtml(fence.join('\n'))}</code></pre>`);
        fence = null;
      } else {
        closeList();
        fence = [];
      }
      continue;
    }
    if (fence) {
      fence.push(raw);
      continue;
    }

    if (!line.trim()) {
      closeList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      closeList();
      const level = heading[1]?.length ?? 1;
      html.push(`<h${level}>${inline(heading[2] ?? '')}</h${level}>`);
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s-*_]*$/.test(line)) {
      closeList();
      html.push('<hr />');
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      closeList();
      html.push(`<blockquote>${inline(quote[1] ?? '')}</blockquote>`);
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      openList('ul');
      html.push(`<li>${inline(bullet[1] ?? '')}</li>`);
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      openList('ol');
      html.push(`<li>${inline(numbered[1] ?? '')}</li>`);
      continue;
    }

    closeList();
    html.push(`<p>${inline(line)}</p>`);
  }

  if (fence) html.push(`<pre><code>${escapeHtml(fence.join('\n'))}</code></pre>`);
  closeList();
  return html.join('');
}
